import React, { useEffect, useState } from "react";
import api from "../../../api/api";
import Swal from "sweetalert2";

import {
    UploadOutlined,
    UserAddOutlined,
    BookOutlined,
    IdcardOutlined,
    HeartOutlined,
    TeamOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";

import {
    Form,
    Input,
    Select,
    Button,
    Card,
    Row,
    Col,
    Typography,
    DatePicker,
    Upload,
} from "antd";

const { Title, Text } = Typography;
const { Option } = Select;

const emptyStudent = {
    user: "",
    full_name: "",
    gender: "",
    previous_class: "",
    grade_class: "",
    division_assigned: "",
    major_subject: "",
    previous_school: "",
    hobit: "",
    date_of_birth: "",
    address: "",
    mother_name: "",
    father_name: "",
    health_status: "",
    parent_contact: "",
    enrollment_date: "",
    semester_status: "",
    status: "",
    student_status: "",
    nationality: "",
    parent_id: "",
    academic_year: "",
    county_of_origin: "",
};

const AdmissionForm = () => {
    const [form] = Form.useForm();

    const [student, setStudent] = useState(emptyStudent);
    const [academicYears, setAcademicYears] = useState([]);
    const [grades, setGrades] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [superParents, setSuperParents] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    const token = localStorage.getItem("access");

    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    useEffect(() => {
        loadAcademicYears();
        loadGrades();
        loadDivisions();
        loadSuperParents();
        loadStudentsUsers();
    }, []);

    const loadAcademicYears = async () => {
        try {
            const res = await api.get(
                "/academic-years/",
                config
            );

            setAcademicYears(res.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    const loadGrades = async () => {
        try {
            const res = await api.get(
                "/api/users/grade-classes/",
                config
            );

            setGrades(res.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    const loadDivisions = async () => {
        try {
            const res = await api.get(
                "/api/users/divisions/",
                config
            );

            setDivisions(res.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    const loadSuperParents = async () => {
        try {
            const res = await api.get(
                "/api/super-parents/",
                config
            );

            setSuperParents(res.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    const loadStudentsUsers = async () => {
        try {
            const res = await api.get(
                "/api/students/",
                config
            );

            setUsers(res.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    const handleStudentUserChange = (userId) => {
        const selectedUser = users.find(
            (user) =>
                String(user.id) === String(userId)
        );

        if (!selectedUser) {
            form.setFieldsValue({
                gender: undefined,
            });

            setStudent((previous) => ({
                ...previous,
                user: userId || "",
                gender: "",
            }));

            return;
        }

        const userFullName =
            selectedUser.username ||
            selectedUser.full_name ||
            selectedUser.name ||
            selectedUser.email ||
            "";

        const userGender =
            selectedUser.gender || "";

        form.setFieldsValue({
            full_name: userFullName,
            gender: userGender || undefined,
        });

        setStudent((previous) => ({
            ...previous,
            user: userId,
            full_name: userFullName,
            gender: userGender,
        }));
    };

    const handleGenderChange = (gender) => {
        setStudent((previous) => ({
            ...previous,
            gender: gender || "",
        }));
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            const formData = new FormData();

            Object.entries(values).forEach(
                ([key, value]) => {
                    if (
                        key !== "student_admission_image" &&
                        value !== undefined &&
                        value !== null
                    ) {
                        formData.append(key, value);
                    }
                }
            );

            if (values.date_of_birth) {
                formData.set(
                    "date_of_birth",
                    values.date_of_birth.format(
                        "YYYY-MM-DD"
                    )
                );
            }

            if (values.enrollment_date) {
                formData.set(
                    "enrollment_date",
                    values.enrollment_date.format(
                        "YYYY-MM-DD"
                    )
                );
            }

            if (
                values.student_admission_image?.length
            ) {
                formData.append(
                    "student_admission_image",
                    values.student_admission_image[0]
                        .originFileObj
                );
            }

            await api.post(
                "/api/users/students/",
                formData,
                config
            );

            Swal.fire({
                icon: "success",
                title: "Admission Successful",
                text:
                    "Student has been admitted successfully.",
                confirmButtonColor: "#0A2A43",
            });

            form.resetFields();
            setStudent(emptyStudent);
        } catch (error) {
            console.log(error.response?.data);

            Swal.fire({
                icon: "error",
                title: "Admission Failed",
                text:
                    error.response?.data?.detail ||
                    "Unable to submit student information",
                confirmButtonColor: "#c0392b",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                background:
                    "linear-gradient(135deg, #f3f6f9 0%, #eef2f5 100%)",
                padding: "24px 18px 40px",
                boxSizing: "border-box",
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: "1180px",
                    margin: "0 auto",
                }}
            >
                {/* PAGE HEADER */}

                <div
                    style={{
                        background:
                            "linear-gradient(135deg, #062A4B 0%, #0A3A61 100%)",
                        borderRadius: "14px",
                        padding: "22px 26px",
                        marginBottom: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "18px",
                        boxShadow:
                            "0 8px 22px rgba(6, 42, 75, 0.14)",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "15px",
                            minWidth: 0,
                        }}
                    >
                        <div
                            style={{
                                width: "46px",
                                height: "46px",
                                minWidth: "46px",
                                borderRadius: "11px",
                                background:
                                    "rgba(246, 201, 68, 0.14)",
                                border:
                                    "1px solid rgba(246, 201, 68, 0.35)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#F6C944",
                                fontSize: "22px",
                            }}
                        >
                            <UserAddOutlined />
                        </div>

                        <div>
                            <div
                                style={{
                                    color: "#F6C944",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    letterSpacing: "1.4px",
                                    marginBottom: "2px",
                                }}
                            >
                                SCHOOL ADMINISTRATION
                            </div>

                            <Title
                                level={2}
                                style={{
                                    margin: 0,
                                    color: "#FFFFFF",
                                    fontSize: "25px",
                                    lineHeight: 1.25,
                                    fontWeight: 700,
                                }}
                            >
                                Student Admission
                            </Title>

                            <Text
                                style={{
                                    display: "block",
                                    color:
                                        "rgba(255,255,255,0.72)",
                                    fontSize: "13px",
                                    marginTop: "3px",
                                }}
                            >
                                Complete the registration
                                information below.
                            </Text>
                        </div>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                            color: "#F6C944",
                            fontSize: "12px",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                        }}
                    >
                        <CheckCircleOutlined />
                        New Registration
                    </div>
                </div>

                {/* FORM */}

                <Card
                    bordered={false}
                    style={{
                        borderRadius: "14px",
                        boxShadow:
                            "0 8px 28px rgba(6, 42, 75, 0.08)",
                        overflow: "hidden",
                    }}
                    bodyStyle={{
                        padding: "22px 24px 26px",
                    }}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        {/* ACADEMIC INFORMATION */}

                        <section
                            style={{
                                marginBottom: "22px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "11px",
                                    marginBottom: "12px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "34px",
                                        height: "34px",
                                        borderRadius: "9px",
                                        background:
                                            "rgba(6, 58, 107, 0.09)",
                                        color: "#063A6B",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "17px",
                                    }}
                                >
                                    <BookOutlined />
                                </div>

                                <div>
                                    <Title
                                        level={4}
                                        style={{
                                            margin: 0,
                                            color: "#062A4B",
                                            fontSize: "17px",
                                        }}
                                    >
                                        Academic Information
                                    </Title>

                                    <Text
                                        style={{
                                            color: "#7b8794",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Select the academic placement
                                        for this student.
                                    </Text>
                                </div>
                            </div>

                            <div
                                style={{
                                    height: "1px",
                                    background: "#edf0f2",
                                    marginBottom: "14px",
                                }}
                            />

                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Academic Year"
                                        name="academic_year"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    "Select academic year",
                                            },
                                        ]}
                                    >
                                        <Select
                                            placeholder="Select academic year"
                                            className="admission-select"
                                        >
                                            {academicYears.map(
                                                (year) => (
                                                    <Option
                                                        key={year.id}
                                                        value={year.id}
                                                    >
                                                        {year.name}
                                                    </Option>
                                                )
                                            )}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Grade Class"
                                        name="grade_class"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    "Select grade class",
                                            },
                                        ]}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="children"
                                            placeholder="Select grade class"
                                            className="admission-select"
                                        >
                                            {grades.map(
                                                (item) => (
                                                    <Option
                                                        key={item.id}
                                                        value={item.id}
                                                    >
                                                        {item.name}
                                                    </Option>
                                                )
                                            )}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Student Account"
                                        name="user"
                                    >
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="Select student account"
                                            optionFilterProp="children"
                                            className="admission-select"
                                            onChange={
                                                handleStudentUserChange
                                            }
                                        >
                                            {users.map(
                                                (user) => (
                                                    <Option
                                                        key={
                                                            user.id
                                                        }
                                                        value={
                                                            user.id
                                                        }
                                                    >
                                                        {user.username ||
                                                            user.full_name ||
                                                            user.email}
                                                    </Option>
                                                )
                                            )}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Division"
                                        name="division_assigned"
                                    >
                                        <Select
                                            placeholder="Select division"
                                            className="admission-select"
                                        >
                                            {divisions.map(
                                                (item) => (
                                                    <Option
                                                        key={item.id}
                                                        value={item.id}
                                                    >
                                                        {item.name}
                                                    </Option>
                                                )
                                            )}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </section>

                        {/* STUDENT INFORMATION */}

                        <section
                            style={{
                                marginBottom: "22px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "11px",
                                    marginBottom: "12px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "34px",
                                        height: "34px",
                                        borderRadius: "9px",
                                        background:
                                            "rgba(15, 109, 171, 0.09)",
                                        color: "#0F6DAB",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "17px",
                                    }}
                                >
                                    <IdcardOutlined />
                                </div>

                                <div>
                                    <Title
                                        level={4}
                                        style={{
                                            margin: 0,
                                            color: "#062A4B",
                                            fontSize: "17px",
                                        }}
                                    >
                                        Student Information
                                    </Title>

                                    <Text
                                        style={{
                                            color: "#7b8794",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Enter the student's personal
                                        information.
                                    </Text>
                                </div>
                            </div>

                            <div
                                style={{
                                    height: "1px",
                                    background: "#edf0f2",
                                    marginBottom: "14px",
                                }}
                            />

                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Full Name"
                                        name="full_name"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    "Enter student full name",
                                            },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Enter student's full name"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Gender"
                                        name="gender"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    "Select student gender",
                                            },
                                        ]}
                                    >
                                        <Select
                                            allowClear
                                            placeholder="Gender auto-fills from account"
                                            className="admission-select"
                                            onChange={
                                                handleGenderChange
                                            }
                                        >
                                            <Option value="Male">
                                                Male
                                            </Option>

                                            <Option value="Female">
                                                Female
                                            </Option>

                                            <Option value="Other">
                                                Other
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="County of Origin"
                                        name="county_of_origin"
                                    >
                                        <Input
                                            placeholder="Enter county of origin"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Previous Class"
                                        name="previous_class"
                                    >
                                        <Input
                                            placeholder="Enter previous class"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Major Subject"
                                        name="major_subject"
                                    >
                                        <Input
                                            placeholder="Enter major subject"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Previous School"
                                        name="previous_school"
                                    >
                                        <Input
                                            placeholder="Enter previous school"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Hobby"
                                        name="hobit"
                                    >
                                        <Input
                                            placeholder="Enter student's hobby"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Date of Birth"
                                        name="date_of_birth"
                                    >
                                        <DatePicker
                                            style={{
                                                width: "100%",
                                            }}
                                            className="admission-input"
                                            placeholder="Select date of birth"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Student Admission Image"
                                        name="student_admission_image"
                                        valuePropName="file"
                                        getValueFromEvent={(e) =>
                                            Array.isArray(e)
                                                ? e
                                                : e?.fileList
                                        }
                                    >
                                        <Upload
                                            beforeUpload={() =>
                                                false
                                            }
                                            maxCount={1}
                                            accept="image/*"
                                        >
                                            <div
                                                style={{
                                                    height: "42px",
                                                    minWidth: "240px",
                                                    padding:
                                                        "0 14px",
                                                    border:
                                                        "1px dashed #cbd5df",
                                                    borderRadius:
                                                        "7px",
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: "9px",
                                                    color: "#52606d",
                                                    cursor:
                                                        "pointer",
                                                    background:
                                                        "#fafbfc",
                                                }}
                                            >
                                                <UploadOutlined
                                                    style={{
                                                        color:
                                                            "#0F6DAB",
                                                        fontSize:
                                                            "16px",
                                                    }}
                                                />

                                                <span
                                                    style={{
                                                        fontSize:
                                                            "12px",
                                                    }}
                                                >
                                                    Upload Student
                                                    Photo
                                                </span>
                                            </div>
                                        </Upload>
                                    </Form.Item>
                                </Col>

                                <Col xs={24}>
                                    <Form.Item
                                        label="Address"
                                        name="address"
                                    >
                                        <Input.TextArea
                                            rows={3}
                                            placeholder="Enter student's current address"
                                            className="admission-textarea"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </section>

                        {/* PARENT INFORMATION */}

                        <section
                            style={{
                                marginBottom: "22px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "11px",
                                    marginBottom: "12px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "34px",
                                        height: "34px",
                                        borderRadius: "9px",
                                        background:
                                            "rgba(79, 174, 34, 0.09)",
                                        color: "#4FAE22",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "17px",
                                    }}
                                >
                                    <TeamOutlined />
                                </div>

                                <div>
                                    <Title
                                        level={4}
                                        style={{
                                            margin: 0,
                                            color: "#062A4B",
                                            fontSize: "17px",
                                        }}
                                    >
                                        Parent / Guardian Information
                                    </Title>

                                    <Text
                                        style={{
                                            color: "#7b8794",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Provide parent or guardian
                                        information.
                                    </Text>
                                </div>
                            </div>

                            <div
                                style={{
                                    height: "1px",
                                    background: "#edf0f2",
                                    marginBottom: "14px",
                                }}
                            />

                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Select Parent"
                                        name="parent_id"
                                    >
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="Select parent account"
                                            optionFilterProp="children"
                                            className="admission-select"
                                        >
                                            {superParents.map(
                                                (parent) => (
                                                    <Option
                                                        key={
                                                            parent.id
                                                        }
                                                        value={
                                                            parent.id
                                                        }
                                                    >
                                                        {parent.username ||
                                                            parent.full_name ||
                                                            parent.email}
                                                    </Option>
                                                )
                                            )}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Mother's Name"
                                        name="mother_name"
                                    >
                                        <Input
                                            placeholder="Enter mother's name"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Father's Name"
                                        name="father_name"
                                    >
                                        <Input
                                            placeholder="Enter father's name"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Parent Contact"
                                        name="parent_contact"
                                    >
                                        <Input
                                            placeholder="07734554345 / 07734453355"
                                            className="admission-input"
                                            maxLength={50}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </section>

                        {/* HEALTH INFORMATION */}

                        <section
                            style={{
                                marginBottom: "22px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "11px",
                                    marginBottom: "12px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "34px",
                                        height: "34px",
                                        borderRadius: "9px",
                                        background:
                                            "rgba(192, 117, 13, 0.09)",
                                        color: "#C0750D",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "17px",
                                    }}
                                >
                                    <HeartOutlined />
                                </div>

                                <div>
                                    <Title
                                        level={4}
                                        style={{
                                            margin: 0,
                                            color: "#062A4B",
                                            fontSize: "17px",
                                        }}
                                    >
                                        Health & Nationality
                                    </Title>

                                    <Text
                                        style={{
                                            color: "#7b8794",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Add health and nationality
                                        information.
                                    </Text>
                                </div>
                            </div>

                            <div
                                style={{
                                    height: "1px",
                                    background: "#edf0f2",
                                    marginBottom: "14px",
                                }}
                            />

                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Health Status"
                                        name="health_status"
                                    >
                                        <Input.TextArea
                                            rows={3}
                                            placeholder="Enter relevant health or medical information"
                                            className="admission-textarea"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Nationality"
                                        name="nationality"
                                    >
                                        <Input
                                            placeholder="Enter nationality"
                                            className="admission-input"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </section>

                        {/* ACADEMIC STATUS */}

                        <section
                            style={{
                                marginBottom: "18px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "11px",
                                    marginBottom: "12px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "34px",
                                        height: "34px",
                                        borderRadius: "9px",
                                        background:
                                            "rgba(246, 201, 68, 0.15)",
                                        color: "#C0750D",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "17px",
                                    }}
                                >
                                    <CalendarOutlined />
                                </div>

                                <div>
                                    <Title
                                        level={4}
                                        style={{
                                            margin: 0,
                                            color: "#062A4B",
                                            fontSize: "17px",
                                        }}
                                    >
                                        Enrollment & Academic Status
                                    </Title>

                                    <Text
                                        style={{
                                            color: "#7b8794",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Set enrollment and academic
                                        status.
                                    </Text>
                                </div>
                            </div>

                            <div
                                style={{
                                    height: "1px",
                                    background: "#edf0f2",
                                    marginBottom: "14px",
                                }}
                            />

                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Enrollment Date"
                                        name="enrollment_date"
                                    >
                                        <DatePicker
                                            style={{
                                                width: "100%",
                                            }}
                                            className="admission-input"
                                            placeholder="Select enrollment date"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Semester"
                                        name="semester_status"
                                    >
                                        <Select
                                            placeholder="Select semester"
                                            className="admission-select"
                                        >
                                            <Option value="First">
                                                First Semester
                                            </Option>

                                            <Option value="Second">
                                                Second Semester
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Student Status"
                                        name="student_status"
                                    >
                                        <Select
                                            placeholder="Select student status"
                                            className="admission-select"
                                        >
                                            <Option value="Regular">
                                                Regular
                                            </Option>

                                            <Option value="Ward">
                                                Dependent
                                            </Option>

                                            <Option value="Scholarship Student">
                                                Scholarship Student
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Enrollment Status"
                                        name="status"
                                    >
                                        <Select
                                            placeholder="Select enrollment status"
                                            className="admission-select"
                                        >
                                            <Option value="Pending">
                                                Pending
                                            </Option>

                                            <Option value="Enrolled">
                                                Enrolled
                                            </Option>

                                            <Option value="Graduated">
                                                Graduated
                                            </Option>

                                            <Option value="Dropped">
                                                Dropped
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </section>

                        {/* SUBMIT */}

                        <div
                            style={{
                                marginTop: "4px",
                                paddingTop: "16px",
                                borderTop:
                                    "1px solid #edf0f2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                    "space-between",
                                gap: "18px",
                                flexWrap: "wrap",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                }}
                            >
                                <CheckCircleOutlined
                                    style={{
                                        color: "#4FAE22",
                                        fontSize: "20px",
                                    }}
                                />

                                <div>
                                    <strong
                                        style={{
                                            display: "block",
                                            color: "#062A4B",
                                            fontSize: "13px",
                                        }}
                                    >
                                        Ready to submit?
                                    </strong>

                                    <span
                                        style={{
                                            color: "#7b8794",
                                            fontSize: "11px",
                                        }}
                                    >
                                        Review the information
                                        before completing the
                                        admission.
                                    </span>
                                </div>
                            </div>

                            <Button
                                htmlType="submit"
                                loading={loading}
                                icon={
                                    !loading && (
                                        <UserAddOutlined />
                                    )
                                }
                                style={{
                                    height: "44px",
                                    minWidth: "235px",
                                    borderRadius: "7px",
                                    border: "none",
                                    background:
                                        "#062A4B",
                                    color: "#FFFFFF",
                                    fontWeight: 600,
                                    boxShadow:
                                        "0 5px 14px rgba(6, 42, 75, 0.18)",
                                }}
                            >
                                {loading
                                    ? "Submitting Admission..."
                                    : "Submit Student Admission"}
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>

            <style>
                {`
                    .ant-form-item {
                        margin-bottom: 13px !important;
                    }

                    .ant-form-item-label {
                        padding-bottom: 4px !important;
                    }

                    .ant-form-item-label > label {
                        color: #344054 !important;
                        font-size: 12px !important;
                        font-weight: 600 !important;
                    }

                    .admission-input,
                    .admission-select {
                        width: 100%;
                    }

                    .admission-input.ant-input,
                    .admission-input.ant-picker {
                        min-height: 40px;
                        border-radius: 7px;
                        border-color: #d8dee5;
                        font-size: 13px;
                    }

                    .admission-input.ant-input:focus,
                    .admission-input.ant-input:hover,
                    .admission-input.ant-picker:hover,
                    .admission-input.ant-picker-focused {
                        border-color: #0F6DAB;
                        box-shadow: 0 0 0 2px rgba(15, 109, 171, 0.08);
                    }

                    .admission-select .ant-select-selector {
                        min-height: 40px !important;
                        border-radius: 7px !important;
                        border-color: #d8dee5 !important;
                        display: flex;
                        align-items: center;
                        font-size: 13px;
                    }

                    .admission-select.ant-select-focused
                        .ant-select-selector,
                    .admission-select:hover
                        .ant-select-selector {
                        border-color: #0F6DAB !important;
                        box-shadow: 0 0 0 2px rgba(15, 109, 171, 0.08) !important;
                    }

                    .admission-textarea {
                        border-radius: 7px !important;
                        border-color: #d8dee5 !important;
                        resize: vertical;
                        font-size: 13px;
                    }

                    .admission-textarea:focus,
                    .admission-textarea:hover {
                        border-color: #0F6DAB !important;
                        box-shadow: 0 0 0 2px rgba(15, 109, 171, 0.08) !important;
                    }

                    .ant-upload {
                        width: 100%;
                    }

                    .ant-upload-select {
                        width: 100%;
                    }

                    @media (max-width: 768px) {
                        .admission-page {
                            padding: 14px !important;
                        }

                        .admission-hero {
                            padding: 18px !important;
                        }

                        .admission-hero-badge {
                            display: none;
                        }
                    }

                    @media (max-width: 600px) {
                        .admission-submit-button {
                            width: 100%;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default AdmissionForm;
